// packages/web/src/components/ui/Button.tsx — misma API, nuevo estilo.
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';
type Size = 'sm' | 'md';

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  className?: string;
}

const VARIANT: Record<Variant, string> = {
  // Verde = acción principal del docente (crear aula). Navy queda para .btn-primary.
  primary: 'bg-emerald-700 hover:bg-emerald-800 text-white border border-emerald-700',
  secondary: 'bg-white hover:bg-slate-50 text-marzano-600 border border-slate-300',
  // Peligro en contorno: no compite con la acción principal.
  danger: 'bg-white hover:bg-red-50 text-red-700 border border-red-300',
  ghost: 'bg-transparent hover:bg-slate-100 text-slate-700 border border-transparent',
};

const SIZE: Record<Size, string> = {
  sm: 'min-h-[36px] px-3 text-sm',
  md: 'min-h-[42px] px-4 text-sm',
};

export function Button({
  variant = 'primary', size = 'md', loading = false, leftIcon, rightIcon, disabled, children, className = '', ...rest
}: Props) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={
        'rounded-lg inline-flex items-center gap-2 justify-center font-semibold transition-colors ' +
        'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-amber-500 ' +
        'disabled:opacity-50 disabled:cursor-not-allowed ' +
        `${VARIANT[variant]} ${SIZE[size]} ${className}`
      }
    >
      {loading ? <Loader2 aria-hidden className="w-4 h-4 animate-spin" /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
}
